import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useAuth} from "../../../contexts/AuthContext";

// Shared top navigation for CFAS pages.
// Usage: <CFASNavbar/>  or  <CFASNavbar userName="..."/>
// Brand colours from the Samaggi guide: #002B1B, #004225, #C3A56A, #F4EFE5.

export interface CFASNavbarProps {
    /** Shown next to the avatar when signed in. Optional. */
    userName?: string;
    /** Override the default sign-out (clears the token and goes to sign-in). */
    onSignOut?: () => void;
}

const LINKS = [
    {label: "Find roles", to: "/cfas/apply/home"},
    {label: "My applications", to: "/cfas/track/home"},
    {label: "Profile", to: "/cfas/hub/register"},
    {label: "My SACC", to: "/cfas/event/home"},
];

function initials(name?: string): string {
    if (!name) return "";
    return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export const CFASNavbar: React.FC<CFASNavbarProps> = ({userName, onSignOut}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {getProfile} = useAuth();
    // Same signal Main uses for auth: a token in localStorage.
    const signedIn = localStorage.getItem("token") !== null;
    const [menuOpen, setMenuOpen] = useState(false);
    const [profileName, setProfileName] = useState(() => (
        userName || (signedIn ? localStorage.getItem("cfasProfileName") || "" : "")
    ));

    useEffect(() => {
        if (!signedIn) {
            setProfileName("");
            return;
        }
        if (userName?.trim()) {
            setProfileName(userName.trim());
            localStorage.setItem("cfasProfileName", userName.trim());
            return;
        }
        const cachedName = localStorage.getItem("cfasProfileName");
        if (cachedName) {
            setProfileName(cachedName);
            return;
        }

        getProfile().then(async (response: Response) => {
            if (!response.ok) return;
            const result = await response.json();
            const name = result.data?.fullName || result.data?.name;
            if (typeof name === "string" && name.trim()) {
                setProfileName(name.trim());
                localStorage.setItem("cfasProfileName", name.trim());
            }
        }).catch((error: unknown) => {
            console.warn("Unable to load the signed-in profile name.", error);
        });
        // The profile is loaded once per signed-in session. The cached name
        // prevents a default account label flashing during route changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signedIn, userName]);

    const go = (to: string) => {
        setMenuOpen(false);
        navigate(to);
    };

    const signOut = () => {
        setMenuOpen(false);
        if (onSignOut) {
            onSignOut();
            return;
        }
        localStorage.removeItem("token");
        localStorage.removeItem("cfasProfileName");
        window.location.href = "/cfas/hub/sign-in";
    };

    const isActive = (to: string) => {
        if (to === "/cfas/apply/home") {
            return location.pathname === "/cfas/apply/home" || location.pathname === "/cfas/roles" || location.pathname.startsWith("/cfas/roles/");
        }
        if (to === "/cfas/hub/register") {
            return location.pathname === "/cfas/hub/register";
        }
        if (to === "/cfas/event/home") {
            return location.pathname.startsWith("/cfas/event/");
        }
        return location.pathname === to;
    };

    return (
        <header className="cfn">
            <style>{CSS}</style>
            <div className="cfn-in">
                <button className="cfn-wm" onClick={() => go("/")} aria-label="CFAS home">
                    <i>SS</i>
                    <span>
                        CFAS
                        <small>SAMAGGI CAREER AND SACC PLATFORM</small>
                    </span>
                </button>

                <nav className="cfn-links" aria-label="Main">
                    {LINKS.map((l) => (
                        <button
                            key={l.to}
                            className={isActive(l.to) ? "on" : ""}
                            aria-current={isActive(l.to) ? "page" : undefined}
                            onClick={() => go(l.to === "/cfas/hub/register" && !signedIn ? "/cfas/hub/sign-in" : l.to)}
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="cfn-right">
                    {signedIn ? (
                        <>
                            <div className="cfn-account">
                                <button
                                    className="cfn-user-menu"
                                    onClick={() => setMenuOpen(!menuOpen)}
                                    aria-expanded={menuOpen}
                                    aria-haspopup="menu"
                                >
                                    <span className="cfn-av">{initials(profileName) || "SS"}</span>
                                    <span className="cfn-user-name">{profileName ? profileName.split(" ")[0] : "Account"}</span>
                                </button>
                                {menuOpen && (
                                    <div className="cfn-account-menu" role="menu">
                                        <button role="menuitem" onClick={() => go("/cfas/hub/home")}>CFAS Hub</button>
                                        <button role="menuitem" onClick={signOut}>Sign out</button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <button className="cfn-cta" onClick={() => go("/cfas/hub/sign-in")}>
                            Sign in / Create profile
                        </button>
                    )}
                    <button
                        className="cfn-burger"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <span/><span/><span/>
                    </button>
                </div>
            </div>

            {menuOpen && (
                <nav className="cfn-mobile" aria-label="Main">
                    {LINKS.map((l) => (
                        <button
                            key={l.to}
                            className={isActive(l.to) ? "on" : ""}
                            aria-current={isActive(l.to) ? "page" : undefined}
                            onClick={() => go(l.to)}
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>
            )}
        </header>
    );
};

const CSS = `
.cfn{
  --deep:#002B1B;--gold:#C3A56A;--gold-hover:#B39258;
  --f-display:"soleil","Soleil","Outfit",system-ui,sans-serif;
  --f-body:"proxima-nova","Proxima Nova","Figtree",system-ui,sans-serif;
  position:sticky;top:0;z-index:100;background:var(--deep);color:#fff;
  border-bottom:1px solid rgba(255,255,255,.08);font-family:var(--f-body);
}
.cfn *{box-sizing:border-box}
.cfn-in{max-width:1120px;margin:0 auto;padding:0 20px;min-height:66px;display:flex;align-items:center;gap:18px}

.cfn-wm{all:unset;cursor:pointer;display:flex;align-items:center;gap:10px;color:#fff}
.cfn-wm i{width:34px;height:34px;border-radius:10px;background:var(--gold);color:var(--deep);display:grid;place-items:center;font-style:normal;font-family:var(--f-display);font-weight:800;font-size:.8rem;letter-spacing:.02em}
.cfn-wm span{font-family:var(--f-display);font-weight:700;letter-spacing:.03em;font-size:1rem;line-height:1.05}
.cfn-wm small{display:block;font-family:var(--f-body);font-weight:400;font-size:.62rem;opacity:.75;letter-spacing:.12em}

.cfn-links{display:none;gap:2px;margin-left:12px}
.cfn-links button,.cfn-mobile button{all:unset;cursor:pointer;color:#fff;padding:8px 12px;border-radius:8px;font-weight:500;font-size:.94rem;opacity:.82;white-space:nowrap}
.cfn-links button:hover,.cfn-mobile button:hover{opacity:1;background:rgba(255,255,255,.08)}
.cfn-links button.on{opacity:1;background:rgba(255,255,255,.12);box-shadow:inset 0 -2px 0 var(--gold)}

.cfn-right{margin-left:auto;display:flex;align-items:center;gap:14px}
.cfn-account{position:relative}
.cfn-user-menu{all:unset;cursor:pointer;display:flex;align-items:center;gap:8px;color:#fff;font-size:.9rem;border-radius:20px;padding:4px 8px 4px 4px}
.cfn-user-menu:hover,.cfn-user-menu:focus-visible{background:rgba(255,255,255,.1)}
.cfn-av{width:32px;height:32px;border-radius:50%;background:var(--gold);color:var(--deep);font-size:.74rem;font-weight:700;display:grid;place-items:center;font-family:var(--f-display)}
.cfn-account-menu{position:absolute;right:0;top:calc(100% + 10px);min-width:170px;padding:8px;background:#fff;border-radius:8px;box-shadow:0 10px 24px rgba(0,0,0,.2);z-index:110}
.cfn-account-menu button{display:block;width:100%;border:0;background:transparent;color:#25332d;text-align:left;cursor:pointer;padding:12px 14px;border-radius:5px;font:inherit;font-size:.98rem}
.cfn-account-menu button:hover,.cfn-account-menu button:focus-visible{background:#f1eee6}
.cfn-right{gap:22px}
.cfn-signin{all:unset;cursor:pointer;color:#fff;opacity:.92;font-family:var(--f-display);font-weight:500;font-size:1rem}
.cfn-signin:hover{opacity:1;text-decoration:underline;text-underline-offset:4px}
.cfn-cta{all:unset;cursor:pointer;box-sizing:border-box;display:inline-flex;align-items:center;justify-content:center;min-height:38px; padding:6px 16px;border-radius:12px;background:var(--gold);color:var(--deep);font-family:var(--f-display);font-weight:700;font-size:1rem;white-space:nowrap;box-shadow:0 6px 18px rgba(195,165,106,.35);transition:background-color .15s,transform .15s,box-shadow .15s}
.cfn-cta:hover{background:var(--gold-hover);transform:translateY(-1px);box-shadow:0 8px 22px rgba(195,165,106,.45)}
@media (max-width:480px){.cfn-right{gap:12px}.cfn-cta{min-height:40px;padding:8px 14px;font-size:.9rem}}

.cfn-burger{all:unset;cursor:pointer;width:38px;height:38px;border-radius:8px;display:grid;place-content:center;gap:4px}
.cfn-burger span{display:block;width:18px;height:2px;border-radius:1px;background:#fff}
.cfn-burger:hover{background:rgba(255,255,255,.08)}

.cfn-mobile{display:grid;gap:2px;padding:6px 12px 14px;border-top:1px solid rgba(255,255,255,.08)}
.cfn-mobile button{padding:12px}
.cfn-mobile button.on{opacity:1;background:rgba(255,255,255,.12);box-shadow:inset 3px 0 0 var(--gold)}

.cfn button:focus-visible{outline:2px solid var(--gold);outline-offset:2px}

@media (min-width:860px){
  .cfn-links{display:flex}
  .cfn-burger,.cfn-mobile{display:none}
}
@media (max-width:480px){
  .cfn-wm small,.cfn-user-name{display:none}
}
@media (prefers-reduced-motion:reduce){.cfn-cta{transition:none}}
`;

export default CFASNavbar;